import React, { useEffect, useState } from "react";
import { getAhaCatalog, saveStandardCourseDetails } from "../../../api/backend";
import { Notice } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { KIND_LABEL, PanelHeader, errorText } from "./common";

const SUBCOURSES = [[1, "Prehospital"], [2, "In-hospital"], [3, "Prehospital & In-hospital"]];
const KINDS = ["DEFAULT", "AHA"];

const initialOf = ({ course, version }) => ({
  title: version.title || course.title || "",
  courseNumber: version.courseNumber || course.courseNumber || "",
  category: course.category || "",
  description: version.description || "",
  prerequisites: version.prerequisites || "",
  totalHours: version.totalHours ?? "",
  maxStudents: version.maxStudents ?? "",
  certificateKinds: version.certificateKinds || [],
  ahaProductKey: version.ahaProductKey || "",
  ahaSubcourseId: version.ahaSubcourseId ?? "",
});

/** Course details, the certificates it issues and, for AHA, the product. Saved on the DRAFT only. */
export default function DetailsPanel({ draft, courseId, onSaved }) {
  const [form, setForm] = useState(() => initialOf(draft));
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const issuesAha = form.certificateKinds.includes("AHA");
  const neverPublished = draft.course.currentVersionId == null;

  useEffect(() => { setForm(initialOf(draft)); }, [draft]);
  useEffect(() => {
    if (!issuesAha || catalog) return;
    getAhaCatalog().then(setCatalog).catch(() => setCatalog({ products: [] }));
  }, [issuesAha, catalog]);

  const products = (catalog?.products || []).filter((p) => p.environment?.active);
  const product = products.find((p) => p.key === form.ahaProductKey);
  const set = (field) => (e) => { setSaved(false); setForm((f) => ({ ...f, [field]: e.target.value })); };
  const toggleKind = (kind, on) => {
    setSaved(false);
    setForm((f) => ({ ...f, certificateKinds: on ? [...new Set([...f.certificateKinds, kind])] : f.certificateKinds.filter((k) => k !== kind) }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: form.title, courseNumber: form.courseNumber, description: form.description, prerequisites: form.prerequisites,
        totalHours: form.totalHours === "" ? null : Number(form.totalHours),
        maxStudents: form.maxStudents === "" ? null : Number(form.maxStudents),
        certificateKinds: form.certificateKinds,
        ahaProductKey: issuesAha ? form.ahaProductKey : "",
        ahaSubcourseId: issuesAha && product?.requiresSubcourse && form.ahaSubcourseId !== "" ? Number(form.ahaSubcourseId) : null,
      };
      if (neverPublished) body.category = form.category;
      await saveStandardCourseDetails(courseId, body);
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(errorText(e, "The details could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel" data-testid="details-panel">
      <PanelHeader title="Details" subtitle="What coordinators see in the Knowledge Bank and what the class is created with." />
      <div className="panel-b">
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Notice kind="bad">
              {error}{" "}
              <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
            </Notice>
          </div>
        )}
        {saved && <div style={{ marginBottom: 16 }}><Notice kind="ok">Saved to the draft. Nothing is live until you publish.</Notice></div>}

        <div className="g3">
          <label className="field">Title<span className="inp"><input value={form.title} onChange={set("title")} required /></span></label>
          <label className="field">Course number<span className="inp"><input value={form.courseNumber} onChange={set("courseNumber")} /></span></label>
          <div style={{ display: "grid", gap: 6 }}>
            <label className="field">Category
              <span className="inp"><input value={form.category} onChange={set("category")} disabled={!neverPublished} /></span>
            </label>
            <span className="h" style={{ fontSize: 12, color: "var(--ga-sub)" }}>{neverPublished ? "For example AHA" : "Fixed once a version is published"}</span>
          </div>
        </div>
        <div className="g2" style={{ marginTop: 14 }}>
          <label className="field">Total hours<span className="inp"><input type="number" value={form.totalHours} onChange={set("totalHours")} /></span></label>
          <label className="field">Seats a class starts with<span className="inp"><input type="number" value={form.maxStudents} onChange={set("maxStudents")} /></span></label>
        </div>
        <label className="field" style={{ marginTop: 14 }}>Description
          <textarea className="ta" rows={3} value={form.description} onChange={set("description")} />
        </label>
        <label className="field" style={{ marginTop: 14 }}>Prerequisites
          <textarea className="ta" rows={2} value={form.prerequisites} onChange={set("prerequisites")} />
        </label>

        <div style={{ fontWeight: 600, marginTop: 24, marginBottom: 8 }}>Certificates this course issues</div>
        <div style={{ display: "grid", gap: 8 }}>
          {KINDS.map((k) => (
            <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={form.certificateKinds.includes(k)} onChange={(e) => toggleKind(k, e.target.checked)} />
              {KIND_LABEL[k]}
            </label>
          ))}
        </div>

        {issuesAha && (
          <div className="g2" style={{ marginTop: 16 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <label className="field">AHA product
                <select className="sel" value={form.ahaProductKey} onChange={set("ahaProductKey")}>
                  <option value="" />
                  {products.map((p) => <option key={p.key} value={p.key}>{p.name}{p.edition ? ` (${p.edition})` : ""}</option>)}
                </select>
              </label>
              {catalog && products.length === 0 && <span className="h" style={{ fontSize: 12, color: "var(--ga-sub)" }}>No active AHA products. Set them up in the AHA Catalog first.</span>}
            </div>
            {product?.requiresSubcourse && (
              <label className="field">ASLS subcourse
                <select className="sel" value={form.ahaSubcourseId} onChange={set("ahaSubcourseId")}>
                  {SUBCOURSES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </label>
            )}
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <button type="button" className="b pri" onClick={save} disabled={saving || !form.title.trim()}>{saving ? "Saving…" : "Save details"}</button>
        </div>
      </div>
    </div>
  );
}
